import { Flex, HStack } from "@chakra-ui/react";
import BigNumber from "bignumber.js";
import React from "react";

import type { InternalTransaction } from "types/api/internalTransaction";
import type { ChainConfig } from "types/multichain";

import config from "configs/app";
import { currencyUnits } from "lib/units";
import { Badge } from "toolkit/chakra/badge";
import { Skeleton } from "toolkit/chakra/skeleton";
import AddressFromTo from "ui/shared/address/AddressFromTo";
import BlockEntity from "ui/shared/entities/block/BlockEntity";
import TxEntity from "ui/shared/entities/tx/TxEntity";
import ListItemMobile from "ui/shared/ListItemMobile/ListItemMobile";
import TxStatus from "ui/shared/statusTag/TxStatus";
import TimeWithTooltip from "ui/shared/time/TimeWithTooltip";
import { TX_INTERNALS_ITEMS } from "ui/tx/internals/utils";

type Props = InternalTransaction & {
  currentAddress?: string;
  isLoading?: boolean;
  showBlockInfo?: boolean;
  chainData?: ChainConfig;
};

const InternalTxsListItem = ({
  type,
  from,
  to,
  value,
  success,
  error,
  created_contract: createdContract,
  transaction_hash: txnHash,
  block_number: blockNumber,
  timestamp,
  currentAddress,
  isLoading,
  showBlockInfo = true,
  chainData,
}: Props) => {
  const typeTitle = TX_INTERNALS_ITEMS.find(({ id }) => id === type)?.title;
  const toData = to ? to : createdContract;

  return (
    <ListItemMobile rowGap={3}>
      <Flex columnGap={2}>
        {typeTitle && (
          <Badge colorPalette="cyan" loading={isLoading}>
            {typeTitle}
          </Badge>
        )}
        <TxStatus
          status={success ? "ok" : "error"}
          errorText={error}
          isLoading={isLoading}
        />
      </Flex>
      <Flex justifyContent="space-between" width="100%">
        <TxEntity
          hash={txnHash}
          isLoading={isLoading}
          fontWeight={700}
          truncation="constant_long"
          chain={chainData}
        />
        <TimeWithTooltip
          timestamp={timestamp}
          isLoading={isLoading}
          color="text.secondary"
          fontWeight="400"
          fontSize="sm"
        />
      </Flex>
      {showBlockInfo && (
        <HStack gap={1}>
          <Skeleton
            bg="transparent"
            loading={isLoading}
            fontSize="sm"
            fontWeight={500}
          >
            Block
          </Skeleton>
          <BlockEntity
            isLoading={isLoading}
            number={blockNumber}
            noIcon
            textStyle="sm"
          />
        </HStack>
      )}
      <AddressFromTo
        from={from}
        to={toData}
        current={currentAddress}
        isLoading={isLoading}
        w="100%"
      />
      <HStack gap={3}>
        <Skeleton
          bg="transparent"
          loading={isLoading}
          fontSize="sm"
          fontWeight={500}
        >
          Value {currencyUnits.ether}
        </Skeleton>
        <Skeleton
          bg="transparent"
          loading={isLoading}
          fontSize="sm"
          color="text.secondary"
          minW={6}
        >
          <span>
            {BigNumber(value)
              .div(BigNumber(10 ** config.chain.currency.decimals))
              .toFormat()}
          </span>
        </Skeleton>
      </HStack>
    </ListItemMobile>
  );
};

export default InternalTxsListItem;
